<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import type { ComplianceDashboard } from '@/types/compliance'
import { fetchPendingTerms } from '@/composables/useTerms'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'ComplianceHome' })

const { t } = useI18n()

const loading = ref(true)
const error = ref<string | null>(null)
const dashboardData = ref<ComplianceDashboard | null>(null)
// Fetched apart from the dashboard so that a failure leaves the other cards standing; the card is hidden then.
const termsPendingCount = ref<number | null>(null)

async function fetchTermsPendingCount() {
  try {
    termsPendingCount.value = (await fetchPendingTerms())?.accounts.length ?? null
  } catch (err) {
    termsPendingCount.value = null
    handleAxiosError(err, { logPrefix: 'ComplianceHome:termsPending' })
  }
}

// Fetch compliance dashboard data
async function fetchDashboard() {
  loading.value = true
  error.value = null

  try {
    const res = await useAxios<null, ComplianceDashboard>(
      'GET',
      '/admin/compliance/dashboard',
      true
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      dashboardData.value = res.data
    } else {
      error.value = t('dashboard.admin.compliance.fetchError')
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'ComplianceHome' })
    error.value = t('dashboard.admin.compliance.fetchError')
  } finally {
    loading.value = false
  }
}

// Stat cards configuration
const statCards = computed(() => {
  if (!dashboardData.value) return []

  const data = dashboardData.value

  return [
    {
      id: 'disclosure',
      title: t('dashboard.admin.compliance.cards.disclosure.title'),
      description: t('dashboard.admin.compliance.cards.disclosure.description'),
      icon: 'mdi-file-document-outline',
      value: data.pending_disclosure_requests,
      secondaryValue: undefined as number | undefined,
      route: { name: 'dashboardAdminDisclosure' },
      secondaryRoute: undefined as { name: string; query: { status: string } } | undefined,
      color: 'amber',
      urgent: data.pending_disclosure_requests > 0
    },
    {
      id: 'abuse',
      title: t('dashboard.admin.compliance.cards.abuse.title'),
      description: t('dashboard.admin.compliance.cards.abuse.description'),
      icon: 'mdi-alert-circle-outline',
      value: data.open_abuse_cases,
      secondaryValue: undefined as number | undefined,
      route: { name: 'dashboardAdminAbuse' },
      secondaryRoute: undefined as { name: string; query: { status: string } } | undefined,
      color: 'red',
      urgent: data.open_abuse_cases > 0
    },
    {
      id: 'suspended',
      title: t('dashboard.admin.compliance.cards.suspended.title'),
      description: t('dashboard.admin.compliance.cards.suspended.description'),
      icon: 'mdi-domain-off',
      value: data.suspended_domains,
      secondaryValue: undefined as number | undefined,
      route: { name: 'dashboardAdminSuspendedDomains' },
      secondaryRoute: undefined as { name: string; query: { status: string } } | undefined,
      color: 'orange',
      urgent: false
    },
    {
      id: 'handles',
      title: t('dashboard.admin.compliance.cards.handles.title'),
      description: t('dashboard.admin.compliance.cards.handles.description'),
      icon: 'mdi-account-clock-outline',
      value: data.pending_verification_handles,
      secondaryValue: data.expired_verification_handles,
      route: { name: 'dashboardAdminHandleRevalidation', query: { status: 'pending' } },
      secondaryRoute: { name: 'dashboardAdminHandleRevalidation', query: { status: 'expired' } },
      color: 'blue',
      urgent: data.expired_verification_handles > 0
    },
    ...(termsPendingCount.value === null
      ? []
      : [
          {
            id: 'terms',
            title: t('dashboard.admin.compliance.cards.terms.title'),
            description: t('dashboard.admin.compliance.cards.terms.description'),
            icon: 'mdi-file-document-alert-outline',
            value: termsPendingCount.value,
            secondaryValue: undefined as number | undefined,
            route: { name: 'dashboardAdminTermsPending' },
            secondaryRoute: undefined as { name: string; query: { status: string } } | undefined,
            color: 'teal',
            urgent: false
          }
        ])
  ]
})

function refresh() {
  fetchDashboard()
  fetchTermsPendingCount()
}

onMounted(refresh)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.title')"
    :subtitle="t('dashboard.admin.compliance.subtitle')"
    icon="mdi-shield-check"
    icon-color="amber"
  >
    <!-- Header Actions -->
    <template #actions>
      <button
        class="dashboard-action-btn"
        :disabled="loading"
        @click="refresh"
      >
        <v-icon icon="mdi-refresh" size="18" :class="{ 'spin': loading }" />
        <span>Aktualisieren</span>
      </button>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Error State -->
    <v-alert
      v-else-if="error"
      type="error"
      variant="tonal"
      class="mb-6"
    >
      {{ error }}
    </v-alert>

    <!-- Dashboard Cards -->
    <div v-else class="stats-grid">
      <router-link
        v-for="card in statCards"
        :key="card.id"
        :to="card.route"
        class="stat-card"
        :class="[`stat-card--${card.color}`, { urgent: card.urgent }]"
      >
        <div class="card-header">
          <div class="card-icon">
            <v-icon :icon="card.icon" size="24" />
          </div>
          <div v-if="card.urgent" class="urgent-badge">
            <v-icon icon="mdi-alert" size="14" />
          </div>
        </div>

        <div class="card-value">
          <span class="value-main">{{ card.value }}</span>
          <router-link
            v-if="card.secondaryValue !== undefined && card.secondaryRoute"
            :to="card.secondaryRoute"
            class="value-secondary clickable"
            @click.stop
          >
            ({{ card.secondaryValue }} {{ t('dashboard.admin.compliance.expired') }})
          </router-link>
          <span v-else-if="card.secondaryValue !== undefined" class="value-secondary">
            ({{ card.secondaryValue }} {{ t('dashboard.admin.compliance.expired') }})
          </span>
        </div>

        <div class="card-info">
          <h3 class="card-title">{{ card.title }}</h3>
          <p class="card-description">{{ card.description }}</p>
        </div>

        <div class="card-arrow">
          <v-icon icon="mdi-chevron-right" size="20" />
        </div>
      </router-link>
    </div>

    <!-- NIS2 Info Section -->
    <div class="info-section">
      <div class="info-header">
        <v-icon icon="mdi-information-outline" size="20" />
        <span>{{ t('dashboard.admin.compliance.info.title') }}</span>
      </div>
      <div class="info-content">
        <p>{{ t('dashboard.admin.compliance.info.description') }}</p>
        <ul class="info-list">
          <li>{{ t('dashboard.admin.compliance.info.point1') }}</li>
          <li>{{ t('dashboard.admin.compliance.info.point2') }}</li>
          <li>{{ t('dashboard.admin.compliance.info.point3') }}</li>
        </ul>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Spin animation for refresh button */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

/* Stat Card */
.stat-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  text-decoration: none;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(120, 163, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

    .card-arrow {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.urgent {
    border-color: rgba(239, 83, 80, 0.3);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #ef5350, #ff5252);
    }
  }

  &--amber .card-icon {
    background: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  &--red .card-icon {
    background: rgba(239, 83, 80, 0.1);
    border-color: rgba(239, 83, 80, 0.2);
    color: #ef5350;
  }

  &--orange .card-icon {
    background: rgba(255, 152, 0, 0.1);
    border-color: rgba(255, 152, 0, 0.2);
    color: #ff9800;
  }

  &--blue .card-icon {
    background: rgba(33, 150, 243, 0.1);
    border-color: rgba(33, 150, 243, 0.2);
    color: #2196f3;
  }

  &--teal .card-icon {
    background: rgba(0, 150, 136, 0.1);
    border-color: rgba(0, 150, 136, 0.2);
    color: #26a69a;
  }
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid;
  border-radius: 12px;
}

.urgent-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(239, 83, 80, 0.15);
  border-radius: 8px;
  color: #ef5350;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.card-value {
  margin-bottom: 12px;
}

.value-main {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.value-secondary {
  display: block;
  font-size: 0.85rem;
  color: #ef5350;
  margin-top: 4px;

  &.clickable {
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      text-decoration: underline;
      color: #ff5252;
    }
  }
}

.card-info {
  flex: 1;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 6px 0;
}

.card-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
  line-height: 1.4;
}

.card-arrow {
  position: absolute;
  right: 20px;
  bottom: 24px;
  color: rgba(200, 218, 255, 0.4);
  opacity: 0;
  transform: translateX(-8px);
  transition: all 0.2s ease;
}

/* Info Section */
.info-section {
  padding: 20px;
  background: rgba(33, 150, 243, 0.06);
  border: 1px solid rgba(33, 150, 243, 0.15);
  border-radius: 12px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  color: #2196f3;
  font-weight: 600;
  font-size: 0.95rem;
}

.info-content {
  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.7);
    margin: 0 0 12px 0;
    line-height: 1.5;
  }
}

.info-list {
  margin: 0;
  padding-left: 20px;

  li {
    font-size: 0.85rem;
    color: rgba(200, 218, 255, 0.6);
    margin-bottom: 6px;
    line-height: 1.4;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .stat-card {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);

    &:hover {
      border-color: rgba(28, 110, 227, 0.2);
    }
  }

  .value-main {
    color: rgba(15, 23, 42, 0.95);
  }

  .card-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .card-description {
    color: rgba(15, 23, 42, 0.6);
  }

  .card-arrow {
    color: rgba(15, 23, 42, 0.3);
  }

  .info-section {
    background: rgba(33, 150, 243, 0.04);
    border-color: rgba(33, 150, 243, 0.12);
  }

  .info-content p {
    color: rgba(15, 23, 42, 0.7);
  }

  .info-list li {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Mobile */
@media (max-width: 600px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .value-main {
    font-size: 2rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .refresh-btn {
    transition: none;
  }

  .spin {
    animation: none;
  }
}
</style>
